import { css } from '@emotion/core';
import styled from '@emotion/styled';
import {
  Box,
  Flex,
  Heading,
  Container as RooContainer,
  Text,
} from '@qga/roo-ui/components';

import { SCREEN_SIZE } from '@shared/v2/constants';

import {
  PrimaryDefault,
  PrimaryPromo,
} from '@/v2/services/sanity/types/api/response/homepageHero';
import { getPrimaryHeroData } from '../../utils';
import Badge from '../Badge';
import Countdown from '../Countdown';
import { PrimaryCTA } from '../CTA';
import Pricing from '../Pricing';

const Container = styled(RooContainer)`
  display: flex;
  flex-direction: row;
  width: 100%;
  position: relative;
  height: 100%;
`;

const Main = styled(Flex)`
  display: flex;
  flex-direction: column;
  color: white;
  position: relative;
  z-index: 1;
  border-top-right-radius: 52px;
  justify-content: space-between;

  // Mobile and up
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin-top: 120px;
  min-height: auto;

  // Tablet and up
  ${SCREEN_SIZE.MD} {
    margin-top: 0;
    min-height: 300px;
  }

  // Desktop
  ${SCREEN_SIZE.LG} {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }
`;

const MainContent = styled(Flex)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  min-height: 132px;

  // Tablet and up
  ${SCREEN_SIZE.MD} {
    justify-content: center;
    min-height: 176px;
  }
`;

const BackgroundImage = styled.div`
  ${({ imgUrl }: { imgUrl: string }) => {
    return css`
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: flex;
      flex-direction: row;
      background-image: url('${imgUrl}');
      border-radius: 0;
      background-repeat: no-repeat;

      // Mobile and up
      background-size: cover;
      background-position: top;

      // Tablet and up
      ${SCREEN_SIZE.MD} {
        background-size: auto;
        border-top-left-radius: 0;
        background-position: center;
        left: 390px;
        right: 0;
      }

      // Desktop
      ${SCREEN_SIZE.LG} {
        border-radius: 10px;
        // Required hack to get around absolute elements not respecting paddings
        right: 8px;
      }
    `;
  }}
`;

interface PrimaryHeroProps {
  primaryPromo: PrimaryPromo;
  primaryDefault: PrimaryDefault;
  isInCarousel?: boolean;
}

const PrimaryHero = ({
  primaryPromo,
  primaryDefault,
  isInCarousel = true,
  ...rest
}: PrimaryHeroProps) => {
  const { headline, tagline, image, cta, variation, pricing, duration, sash } =
    getPrimaryHeroData(primaryPromo, primaryDefault);
  const { label, link, external } = cta ?? {};

  const isBadgeVisible = sash?.variation !== 'none';
  const showCta = !!(label && link);

  return (
    <Box bg="greys.porcelain" width="100%" p={0} {...rest}>
      <Container px={[0, 0, 2]}>
        <Main
          width={['100%', '440px']}
          bg="brand.primary"
          p={[4, 4, 6]}
          pt={[9, isBadgeVisible ? 9 : 6]}
        >
          {isBadgeVisible && <Badge sash={sash} duration={duration} />}
          <MainContent>
            <Heading.h2
              fontWeight={400}
              fontSize="28px"
              lineHeight="40px"
              letterSpacing={0}
              py={0}
              my={0}
            >
              {headline}
            </Heading.h2>
            {!!tagline && (
              <Text fontSize="sm" mt={1}>
                {tagline}
              </Text>
            )}
            {variation === 'price' && (
              <Box mt={4}>
                <Pricing {...pricing} />
              </Box>
            )}
            {variation === 'countdownTimer' && (
              <Box mt={4}>
                <Countdown duration={duration} />
              </Box>
            )}
          </MainContent>
          {showCta && (
            <Flex mt={[6, 5]}>
              <PrimaryCTA
                href={link}
                external={external}
                variant="primary"
                data-testid="PRIMARY_HERO_CTA"
                isInCarousel={isInCarousel}
              >
                <Text fontWeight="bold" fontSize="sm" lineHeight="0">
                  {label}
                </Text>
              </PrimaryCTA>
            </Flex>
          )}
        </Main>
        <BackgroundImage imgUrl={image.url}>&nbsp;</BackgroundImage>
      </Container>
    </Box>
  );
};

export default PrimaryHero;
